import { motion } from "framer-motion";
import { Truck, Package, MapPin, Clock } from "lucide-react";
import PageShell from "@/components/PageShell";
import Seo from "@/components/Seo";

const METHODS = [
  { name: "Standard Ground", time: "5–7 business days", price: "$6.99" },
  { name: "Expedited", time: "2–3 business days", price: "$14.99" },
  { name: "Free Standard", time: "5–7 business days", price: "Free on orders $150+" },
];

const SECTIONS = [
  ["Processing time", "Orders are processed within 1–2 business days. Orders placed on weekends or holidays are processed the next business day. You will receive an email confirmation when your order ships."],
  ["Shipping area", "We ship within the continental United States only. We do not ship to Alaska, Hawaii, Puerto Rico, US territories, PO boxes, or any international destination."],
  ["Packaging", "All vials are shipped in protective, contamination-resistant packaging with tamper-evident seals. Vials ship unlabeled, consistent with their status as research reagents."],
  ["Tracking", "A tracking number is provided by email when your order is dispatched. You can use this number on the carrier's website to follow your shipment."],
  ["Lost or damaged shipments", "Risk of loss passes to the carrier upon dispatch. If your shipment arrives damaged or does not arrive, contact us at orders@lab2o.com within 7 days of the expected delivery date and we will work with you to replace or refund the order."],
];

export default function Shipping() {
  return (
    <PageShell
      testid="shipping-page"
      overline={<span className="flex items-center gap-2"><Truck className="w-3.5 h-3.5" /> Delivery</span>}
      title="Shipping and Delivery"
      intro="How we pack, ship, and track your research reagents. Continental US only."
    >
      <Seo title="Shipping & Delivery, USA | Lab2o" path="/shipping" description="US shipping options for Lab2o lab water, with tracking on every order." />

      <div className="mt-12 grid sm:grid-cols-3 gap-5" data-testid="shipping-methods">
        {METHODS.map((m) => (
          <motion.div
            key={m.name}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            className="border border-slate-200 bg-white p-6"
            data-testid={`shipping-method-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <Package className="w-5 h-5 text-slate-400 mb-3" />
            <h2 className="font-chivo font-bold text-lg text-slate-950">{m.name}</h2>
            <p className="mt-1 text-sm text-slate-600 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-slate-400" />{m.time}</p>
            <p className="mt-2 font-chivo font-extrabold text-xl text-slate-950">{m.price}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-14 max-w-3xl">
        {SECTIONS.map(([title, body]) => (
          <div key={title} className="py-6 border-t border-slate-200" data-testid={`shipping-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
            <h2 className="font-chivo font-bold text-lg text-slate-950 flex items-center gap-2">
              {title === "Shipping area" && <MapPin className="w-4 h-4 text-slate-400" />}
              {title}
            </h2>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
          </div>
        ))}
      </div>
    </PageShell>
  );
}
